import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone, MapPin, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/vip-experience", label: "VIP Experience" },
  { path: "/testimonials", label: "Testimonials" },
  { path: "/contact", label: "Contact" },
];

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen bg-vip-black">
      {/* Header */}
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "glass-heavy py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex-shrink-0"
            >
              <span className="font-heading text-xs sm:text-sm md:text-base font-bold tracking-tight text-white whitespace-nowrap">
                surgicalcarecenter.online
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-6" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`nav-link font-body text-sm uppercase tracking-widest transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-neon-cyan active"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden xl:flex items-center gap-4">
              <Link to="/appointments">
                <Button
                  data-testid="header-book-btn"
                  className="rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-6 py-3 text-sm shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all duration-300"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book VIP
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="xl:hidden p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          data-testid="mobile-menu"
          className={`xl:hidden absolute top-full left-0 right-0 glass-heavy transition-all duration-300 ${
            isMobileMenuOpen
              ? "opacity-100 visible"
              : "opacity-0 invisible pointer-events-none"
          }`}
        >
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`font-body text-base uppercase tracking-widest py-2 transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-neon-cyan"
                    : "text-gray-300"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/appointments" className="mt-4">
              <Button
                data-testid="mobile-book-btn"
                className="w-full rounded-full bg-white text-black hover:bg-neon-cyan font-bold uppercase tracking-widest px-6 py-3 text-sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book VIP Appointment
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer data-testid="main-footer" className="bg-vip-paper border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-white">
                surgicalcarecenter.online
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Surgery Center LLC - Premier surgical care center providing expert medical services. Board-certified physicians ensuring your safety and comfort.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h4 className="font-heading text-lg text-white">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-gray-400 hover:text-neon-cyan text-sm transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h4 className="font-heading text-lg text-white">Contact</h4>
              <div className="space-y-3">
                <a
                  href="tel:8668412730"
                  className="flex items-center gap-3 text-gray-400 hover:text-neon-cyan transition-colors duration-300"
                >
                  <Phone className="w-4 h-4 text-neon-pink" />
                  <span className="text-sm">866.841.2730</span>
                </a>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-neon-pink mt-1 flex-shrink-0" />
                  <span className="text-sm">
                    <span className="text-white/70 block mb-1">Main Office:</span>
                    600 Peachtree St NE Ste 410<br />
                    Atlanta, GA 30308
                  </span>
                </div>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-neon-cyan mt-1 flex-shrink-0" />
                  <span className="text-sm">
                    <span className="text-white/70 block mb-1">Headquarters:</span>
                    930 E Old Harmony Rd Ste C<br />
                    Newark, DE 19713
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <span className="text-sm">NPI: <a href="https://npiregistry.cms.hhs.gov/provider-view/1952213472" target="_blank" rel="noopener noreferrer" className="text-neon-cyan hover:text-neon-pink transition-colors">1952213472</a></span>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="space-y-4">
              <h4 className="font-heading text-lg text-white">Hours</h4>
              <div className="text-gray-400 text-sm space-y-1">
                <p>Monday - Friday: 9AM - 5PM</p>
                <p>Saturday - Sunday: 11AM - 4PM</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs">
              &copy; {new Date().getFullYear()} Surgery Center LLC. All rights reserved.
            </p>
            <div className="flex gap-6 text-xs text-gray-500">
              <span className="hover:text-neon-cyan cursor-pointer transition-colors">Privacy Policy</span>
              <span className="hover:text-neon-cyan cursor-pointer transition-colors">Terms of Service</span>
              <span className="hover:text-neon-cyan cursor-pointer transition-colors">HIPAA Compliance</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Book Button (Mobile) */}
      <div className="xl:hidden fixed bottom-6 right-6 z-40">
        <Link to="/appointments">
          <Button
            data-testid="floating-book-btn"
            className="rounded-full bg-neon-pink text-white hover:bg-neon-cyan hover:text-black w-14 h-14 p-0 shadow-[0_0_30px_rgba(255,0,255,0.5)] hover:shadow-[0_0_40px_rgba(0,240,255,0.6)] transition-all duration-300"
            aria-label="Book Appointment"
          >
            <Calendar className="w-6 h-6" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
